import React from 'react';
import { useQuestionRubric } from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import RubricPreview from '@/components/staffer/ExerciseBuilder/Rubric/EditRubricCard/RubricPreview';

interface IQuestionRubricProps {
  inSlide?: boolean;
  questionId: number;
}

const ANSWER_KEY_CLASSES = {
  cell: '!tw-max-w-[1rem] !tw-h-auto tw-break-words',
  criterion_column: '!tw-pl-4 !tw-pt-4',
  header: '!tw-w-auto tw-break-words',
  score_column: '!tw-pt-2',
};

// PLAT-17600: force size/spacing on rich-text tags to beat DB slide themes and `.slide p { 1.2em }`.
const SLIDE_DESCENDANT_TEXT = '[&_p]:!tw-text-[8px] [&_p]:!tw-m-0 [&_p]:!tw-p-0 '
  + '[&_li]:!tw-text-[8px] [&_ul]:!tw-text-[8px] [&_ol]:!tw-text-[8px]';

// PLAT-17600: analytic-only slide styling. Fixed layout stops the narrow 20% level columns from
// crushing words mid-word; cell/criterion overrides reclaim padding and cancel the global reset's
// `td { vertical-align: baseline }` / `body { line-height: 1 }` so level text keeps its baseline.
const ANSWER_KEY_ANALYTIC_SLIDE_CLASSES = {
  ...ANSWER_KEY_CLASSES,
  cell: `!tw-h-auto tw-align-top tw-px-1 tw-py-2 ${SLIDE_DESCENDANT_TEXT}`,
  container: 'tw-table-fixed',
  criterion_column: `!tw-pt-2 !tw-pl-2 tw-leading-[1.4] ${SLIDE_DESCENDANT_TEXT}`,
};

const ANSWER_KEY_ANALYTIC_COLUMN_SIZES = { criterion: '20%', level: '20%' };

const withTextScale = (classes: typeof ANSWER_KEY_CLASSES & { container?: string }) => ({
  ...classes,
  cell: `${classes.cell} !tw-text-[8px]`,
  header: `${classes.header} !tw-text-[8px]`,
});

const QuestionRubric = ({ inSlide = false, questionId }: IQuestionRubricProps) => {
  const { data, isLoading } = useQuestionRubric({ questionId });

  if (isLoading || !data) return null;

  const classes = inSlide ? withTextScale(ANSWER_KEY_CLASSES) : ANSWER_KEY_CLASSES;
  const analyticClasses = inSlide ? withTextScale(ANSWER_KEY_ANALYTIC_SLIDE_CLASSES) : ANSWER_KEY_CLASSES;

  return (
    <div className="tw-mt-4 tw-w-full">
      <RubricPreview
        analyticColumnSizes={ANSWER_KEY_ANALYTIC_COLUMN_SIZES}
        analyticRubricProps={{ classes: analyticClasses, hasHeaders: true }}
        holisticRubricProps={{ classes, hasHeaders: true }}
        rubric={data}
      />
    </div>
  );
};

export default QuestionRubric;
